/**
 * Uploading a supplier invoice, and turning it into a draft.
 *
 * The file goes to storage and a supplier_invoices row records what the platform
 * managed to read off it. That row is a DRAFT: it never appears in Cost of Sales, the
 * P&L or any ledger. Only the expenses rows a person confirms do. That separation is
 * the point, because a reader that quietly booked what it thought it saw would put
 * numbers in the accounts that nobody chose.
 */

import { supabase } from '../lib/supabase'
import { apiHeadersOrThrow } from '../lib/authHeader'
import { ParsedInvoice, ParsedInvoiceLine, parseInvoiceText, lastAmountIn } from '../lib/invoiceParse'
import { matchVehiclesInText } from '../lib/invoiceVehicleMatch'
import type { FleetVehicle } from '../lib/invoiceVehicleMatch'

const BUCKET = 'supplier-invoices'

const PDFJS_VERSION = '3.11.174'
const PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/' + PDFJS_VERSION + '/pdf.min.js'
const PDFJS_WORKER =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/' + PDFJS_VERSION + '/pdf.worker.min.js'

export interface DealMatch {
  id: string
  label: string
  /** The invoice line key this vehicle was matched from. */
  key: string
  vin: string
  stockNumber: string
}

export interface ReadResult {
  text: string
  parsed: ParsedInvoice
  /** Invoice line key ('vin:...' or 'stock:...') to the deal it belongs to. */
  matches: Record<string, DealMatch>
  /** Vehicles the invoice named that we do not hold, described as they appeared. */
  unknownVins: string[]
}

let pdfjsPromise: Promise<unknown> | null = null

function loadPdfJs(): Promise<unknown> {
  const w = window as unknown as Record<string, unknown>
  if (w.pdfjsLib) return Promise.resolve(w.pdfjsLib)
  if (pdfjsPromise) return pdfjsPromise

  pdfjsPromise = new Promise((resolve, reject) => {
    const tag = document.createElement('script')
    tag.src = PDFJS_SRC
    tag.async = true
    tag.onload = () => {
      const lib = (window as unknown as Record<string, any>).pdfjsLib
      if (!lib) {
        reject(new Error('The PDF reader loaded but did not start.'))
        return
      }
      lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER
      resolve(lib)
    }
    tag.onerror = () => reject(new Error('Could not load the PDF reader. Check the connection and try again.'))
    document.head.appendChild(tag)
  })

  return pdfjsPromise
}

async function pdfText(file: File): Promise<string> {
  const lib = (await loadPdfJs()) as any
  const buffer = await file.arrayBuffer()
  const doc = await lib.getDocument({ data: buffer }).promise
  const pages: string[] = []
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n)
    const content = await page.getTextContent()
    // Rebuild lines from the y position of each run. A PDF has no lines, only pieces of
    // text at coordinates, and an invoice read as one long run loses which amount sits
    // beside which VIN.
    const rows = new Map<number, { x: number; s: string }[]>()
    for (const item of content.items as any[]) {
      if (!item || typeof item.str !== 'string' || !item.str.trim()) continue
      const y = Math.round(item.transform[5])
      if (!rows.has(y)) rows.set(y, [])
      const bucket = rows.get(y) as { x: number; s: string }[]
      bucket.push({ x: item.transform[4], s: item.str })
    }
    const ys = [...rows.keys()].sort((a, b) => b - a)
    for (const y of ys) {
      const parts = rows.get(y) as { x: number; s: string }[]
      parts.sort((a, b) => a.x - b.x)
      pages.push(parts.map((p) => p.s).join(' ').replace(/\s{2,}/g, ' ').trim())
    }
  }
  return pages.join('\n')
}

/* Less than this many non-space characters from a PDF means there is no real text
   layer - a scan, or a page that is all image with a stray caption. */
const MIN_PDF_TEXT = 40

/* The server reader: the PDF's own text, or the page read by the vision model. */
async function serverText(file: File): Promise<{ text: string; error: string | null }> {
  try {
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = () => reject(new Error('That file could not be opened.'))
      reader.readAsDataURL(file)
    })
    const res = await fetch('/api/documents/read-text', {
      method: 'POST',
      headers: await apiHeadersOrThrow(),
      body: JSON.stringify({ fileBase64: dataUrl, contentType: file.type }),
    })
    const body = await res.json().catch(() => null)
    if (res.ok && body && body.success && body.text) return { text: String(body.text), error: null }
    return { text: '', error: (body && body.error) || 'The document could not be read.' }
  } catch (err) {
    return { text: '', error: (err as Error).message || 'The document could not be read.' }
  }
}

/** Why the last extractText call came back empty, for the panel to show. */
export let lastExtractError: string | null = null

export async function extractText(file: File): Promise<string> {
  lastExtractError = null
  const name = file.name.toLowerCase()
  if (/\.(txt|csv|tsv|md)$/.test(name) || file.type.startsWith('text/')) return file.text()

  /* 5 Nov 2026: a photo or a scanned PDF used to return nothing here and fill nothing.
     A PDF with its own text is still read in the browser, exactly as before - the
     parser's line layout was tuned on that output. Anything else (a photo, a scan, or
     a PDF pdf.js could not open because the CDN was unreachable) goes to the server. */
  if (name.endsWith('.pdf') || file.type === 'application/pdf') {
    let text = ''
    try {
      text = await pdfText(file)
    } catch {
      text = ''
    }
    if (text.replace(/\s/g, '').length >= MIN_PDF_TEXT) return text
  }
  const read = await serverText(file)
  lastExtractError = read.error
  return read.text
}

/**
 * Looks up the vehicles an invoice named, by VIN and by stock number.
 *
 * Both, because the stock the platform is carrying today was loaded from EasyCars
 * without VINs. A VIN-only match would find nothing and the feature would look broken
 * when the gap is in the data, not the reader.
 */
export async function matchVehicles(
  invoiceLines: ParsedInvoiceLine[],
  textLines: string[] = [],
): Promise<{ matches: Record<string, DealMatch>; unknown: string[]; extraLines: ParsedInvoiceLine[] }> {
  const vins = [...new Set(invoiceLines.filter((l) => l.vin).map((l) => l.vin.toUpperCase()))]
  const stocks = [
    ...new Set(invoiceLines.filter((l) => !l.vin && l.stockNumber).map((l) => l.stockNumber)),
  ]

  const cols =
    'id, stock_number, autograb_vin, vehicle_vin, registration_number, ' +
    'autograb_year, autograb_make, autograb_model, vehicle_year, vehicle_make, vehicle_model'

  const queries = []
  if (vins.length) {
    /* Winnie, 11 Sep section 2: VIN first. Both VIN columns, because a car keyed in by
       hand carries vehicle_vin and only a looked-up one carries autograb_vin - searching
       one of them was quietly missing half the yard. VINs are already pattern-checked as
       [A-HJ-NPR-Z0-9]{17}, so there is nothing here to quote. */
    const list = vins.join(',')
    queries.push(
      supabase
        .from('deals')
        .select(cols)
        .or('autograb_vin.in.(' + list + '),vehicle_vin.in.(' + list + ')')
        .is('deleted_at', null),
    )
  }
  if (stocks.length) {
    queries.push(supabase.from('deals').select(cols).in('stock_number', stocks).is('deleted_at', null))
  }
  const results = await Promise.all(queries)
  for (const r of results) {
    if (r.error) throw r.error
  }

  const byVin: Record<string, Record<string, unknown>> = {}
  const byStock: Record<string, Record<string, unknown>> = {}
  for (const r of results) {
    for (const row of (r.data || []) as unknown as Record<string, unknown>[]) {
      for (const v of [row.autograb_vin, row.vehicle_vin]) {
        const vin = String(v || '').toUpperCase()
        if (vin) byVin[vin] = row
      }
      const stock = row.stock_number == null ? '' : String(row.stock_number)
      if (stock) byStock[stock] = row
    }
  }

  const describe = (row: Record<string, unknown>) => {
    const parts = [
      row.autograb_year || row.vehicle_year,
      row.autograb_make || row.vehicle_make,
      row.autograb_model || row.vehicle_model,
    ]
      .map((p) => (p == null ? '' : String(p).trim()))
      .filter(Boolean)
    const stock = row.stock_number == null ? '' : String(row.stock_number)
    const vin = String(row.autograb_vin || row.vehicle_vin || '')
    return (stock ? '#' + stock + ' ' : '') + (parts.join(' ') || 'Vehicle') + (vin ? ' - ' + vin : '')
  }

  const matches: Record<string, DealMatch> = {}
  for (const line of invoiceLines) {
    const row = line.vin ? byVin[line.vin.toUpperCase()] : byStock[line.stockNumber]
    if (!row) continue
    matches[line.key] = {
      id: String(row.id),
      label: describe(row),
      key: line.key,
      vin: String(row.autograb_vin || row.vehicle_vin || ''),
      stockNumber: row.stock_number == null ? '' : String(row.stock_number),
    }
  }

  /* Winnie, 11 Sep section 2: "VIN match first, Rego as fallback (rego can change -
     re-plated vehicles, personalised plates)." Only for the lines VIN and stock number
     could not place, and only against plates we actually hold: a plate pulled out of free
     text by pattern would turn consignment and job numbers into vehicles. */
  const stillMissing = invoiceLines.filter((l) => !matches[l.key])
  if (stillMissing.length) {
    const { data: plated } = await supabase
      .from('deals')
      .select(cols)
      .not('registration_number', 'is', null)
      .is('deleted_at', null)

    const rows = (plated || []) as unknown as Record<string, unknown>[]
    const fleet: FleetVehicle[] = rows.map((row) => ({
      dealId: String(row.id),
      vin: null,
      rego: row.registration_number ? String(row.registration_number) : null,
      label: describe(row),
    }))
    const byDeal: Record<string, Record<string, unknown>> = {}
    for (const row of rows) byDeal[String(row.id)] = row

    for (const line of stillMissing) {
      const found = matchVehiclesInText(String((line as any).text || ''), fleet)
      /* One plate on the line is an answer. Two is a question, and a question is not a
         match - the line is left for a person, which is what unmatched() is for. */
      if (found.matches.length !== 1) continue
      const hit = found.matches[0]
      const row = byDeal[hit.dealId] || {}
      matches[line.key] = {
        id: hit.dealId,
        label: hit.label + ' - matched on rego ' + hit.matchedValue,
        key: line.key,
        vin: String(row.autograb_vin || row.vehicle_vin || ''),
        stockNumber: row.stock_number == null ? '' : String(row.stock_number),
      }
    }
  }

  /* And the case the reader cannot raise a line for at all: a row that names its car by
     plate only. The parser builds a vehicle line from a VIN or a stock number, so a
     freight invoice reading "Transport Sydney to Brisbane - Mazda CX-9, rego EHM56A" left
     no line for the plate fallback to sit on. The plates we hold are searched for across
     the whole invoice, and a row that names one becomes a line of its own. */
  const extraLines: ParsedInvoiceLine[] = []
  if (textLines.length) {
    const { data: plated } = await supabase
      .from('deals')
      .select(cols)
      .not('registration_number', 'is', null)
      .is('deleted_at', null)

    const rows = (plated || []) as unknown as Record<string, unknown>[]
    const fleet: FleetVehicle[] = rows.map((row) => ({
      dealId: String(row.id),
      vin: null,
      rego: row.registration_number ? String(row.registration_number) : null,
      label: describe(row),
    }))
    const byDeal: Record<string, Record<string, unknown>> = {}
    for (const row of rows) byDeal[String(row.id)] = row

    const already = new Set(Object.values(matches).map((m) => m.id))
    for (const text of textLines) {
      const found = matchVehiclesInText(String(text || ''), fleet)
      // Two plates on one printed row is a question, not a match. Left for a person.
      if (found.matches.length !== 1) continue
      const hit = found.matches[0]
      if (already.has(hit.dealId)) continue
      already.add(hit.dealId)
      const row = byDeal[hit.dealId] || {}
      const key = 'rego:' + hit.matchedValue
      if (matches[key]) continue
      extraLines.push({
        key,
        vin: String(row.autograb_vin || row.vehicle_vin || ''),
        stockNumber: row.stock_number == null ? '' : String(row.stock_number),
        amountCents: lastAmountIn(text),
        text,
      })
      matches[key] = {
        id: hit.dealId,
        label: hit.label + ' - matched on rego ' + hit.matchedValue,
        key,
        vin: String(row.autograb_vin || row.vehicle_vin || ''),
        stockNumber: row.stock_number == null ? '' : String(row.stock_number),
      }
    }
  }

  const unknown: string[] = []
  for (const line of invoiceLines) {
    if (matches[line.key]) continue
    unknown.push(line.vin || (line.stockNumber ? 'Stock ' + line.stockNumber : 'An unidentified line'))
  }

  return { matches, unknown, extraLines }
}

export async function readInvoiceFile(file: File): Promise<ReadResult> {
  const text = await extractText(file)
  const parsed = parseInvoiceText(text)
  if (!text.trim() && lastExtractError) parsed.notes.unshift(lastExtractError)
  const { matches, unknown, extraLines } = await matchVehicles(parsed.lines, parsed.textLines)
  /* The plate-only rows join the vehicle lines, so everything downstream - the panel, the
     split form, the running total - sees one list of what this invoice is about. */
  if (extraLines.length) {
    parsed.lines = [...parsed.lines, ...extraLines]
    /* The parser was right when it said so and is wrong now: it had no VIN and no stock
       number, but the plates placed the cars. Leaving the note up tells the user to do by
       hand what the screen has already done. */
    parsed.notes = parsed.notes.filter(
      (n) => !/No VIN or stock number appears on this invoice/i.test(n),
    )
    parsed.notes.push(
      extraLines.length +
        (extraLines.length === 1
          ? ' vehicle was matched on its registration rather than a VIN. Plates change hands, so check it is the right car.'
          : ' vehicles were matched on their registration rather than a VIN. Plates change hands, so check they are the right cars.'),
    )
  }
  return { text, parsed, matches, unknownVins: unknown }
}

function safeName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]/g, '_').slice(-80)
}

/**
 * Stores the file and the draft. Returns the supplier_invoices id so the confirmed
 * expenses rows can point back at the invoice they came from, and the stored path so an
 * upload that is abandoned can take its file with it.
 */
export async function saveSupplierInvoice(
  file: File,
  parsed: ParsedInvoice,
  userId: string | null,
): Promise<{ id: string; filePath: string | null }> {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  const path = stamp + '-' + safeName(file.name)

  let filePath: string | null = null
  const upload = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false })
  if (upload.error) {
    // Losing the file copy is not a reason to lose the reading. The draft still saves,
    // and the note says the original did not land so nobody assumes it is filed.
    parsed.notes.push('The file itself could not be stored: ' + upload.error.message)
  } else {
    filePath = upload.data ? upload.data.path : path
  }

  const { data, error } = await supabase
    .from('supplier_invoices')
    .insert({
      supplier: parsed.supplier || null,
      /* Its own column, not folded into the name. Winnie, 13 Sep: this is what lets the
         next invoice from the same business match this one even when the free-text name
         is spelled three different ways. */
      supplier_abn: parsed.supplierAbn || null,
      invoice_ref: parsed.invoiceRef || null,
      invoice_date: parsed.invoiceDate || null,
      due_date: parsed.dueDate || null,
      total_amount: parsed.totalCents === null ? null : parsed.totalCents / 100,
      gst_amount: parsed.gstCents === null ? null : parsed.gstCents / 100,
      subtotal_amount:
        parsed.totalCents === null
          ? null
          : (parsed.totalCents - (parsed.gstCents || 0)) / 100,
      file_path: filePath,
      file_name: file.name,
      file_type: file.type || null,
      source: 'upload',
      extraction_status: parsed.quality,
      extraction_note: parsed.notes.join(' '),
      extracted: parsed as unknown as Record<string, unknown>,
      created_by: userId,
    })
    .select('id')
    .single()

  if (error) throw error
  return { id: String((data as { id: string }).id), filePath }
}

/**
 * Throws away an upload that was never confirmed: the stored file first, then the draft.
 *
 * Only ever called while the draft is still a draft. Once it has been confirmed the
 * storage policy refuses the delete anyway, because the file is then the evidence behind
 * expenses rows that are in the accounts.
 *
 * The file going first is deliberate. If the row went first and the file delete failed,
 * the file would be orphaned with nothing left pointing at it.
 */
export async function discardInvoice(id: string, filePath: string | null): Promise<void> {
  if (filePath) {
    const { error } = await supabase.storage.from(BUCKET).remove([filePath])
    if (error) throw error
  }
  const { error } = await supabase.from('supplier_invoices').delete().eq('id', id)
  if (error) throw error
}

/**
 * A link that opens the stored invoice.
 *
 * The bucket is private, so this is a signed URL rather than a public one, and it is
 * minted on the click rather than held on the row: a signed URL is a key, and a key that
 * sits in a table of costs for an hour is a key that has been handed out.
 *
 * Returns null when the row never had a file - Winnie, 13 Sep: "Rows with no invoice_id
 * (manually entered, no document uploaded) show no view link - nothing to view, don't
 * show a dead/disabled icon."
 */
export async function invoiceFileUrl(filePath: string | null, seconds = 300): Promise<string | null> {
  if (!filePath) return null
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(filePath, seconds)
  if (error) throw error
  return data?.signedUrl || null
}

/** Called once the expenses rows exist, so the draft stops asking to be dealt with. */
export async function markAllocated(id: string): Promise<void> {
  const { error } = await supabase
    .from('supplier_invoices')
    .update({ allocated_at: new Date().toISOString(), extraction_status: 'confirmed' })
    .eq('id', id)
  if (error) throw error
}

export interface DraftInvoice {
  id: string
  supplier: string
  invoiceRef: string
  invoiceDate: string | null
  totalAmount: number | null
  fileName: string
  status: string
  createdAt: string
}

export async function listDraftInvoices(): Promise<DraftInvoice[]> {
  const { data, error } = await supabase
    .from('supplier_invoices')
    .select('id, supplier, invoice_ref, invoice_date, total_amount, file_name, extraction_status, created_at')
    .is('allocated_at', null)
    .order('created_at', { ascending: false })

  if (error) throw error

  return ((data || []) as Record<string, unknown>[]).map((row) => ({
    id: String(row.id),
    supplier: String(row.supplier || ''),
    invoiceRef: String(row.invoice_ref || ''),
    invoiceDate: row.invoice_date ? String(row.invoice_date).slice(0, 10) : null,
    totalAmount: row.total_amount == null ? null : Number(row.total_amount),
    fileName: String(row.file_name || ''),
    status: String(row.extraction_status || ''),
    createdAt: String(row.created_at || ''),
  }))
}
